/**
 * Main Chat Interface Component
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Avatar,
  Fab,
  Zoom,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WorkIcon from '@mui/icons-material/Work';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PublicIcon from '@mui/icons-material/Public';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';

import useAuthStore from '../store/authStore';
import useChatStore from '../store/chatStore';
import Sidebar from './Sidebar';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ExportConversationButton from './ExportConversationButton';
import { useTranslation } from 'react-i18next';

const DRAWER_WIDTH = 280;

export default function ChatInterface() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['common', 'chat']);

  const { user, logout } = useAuthStore();
  const { loadSessions, cleanup, currentSession, messages } = useChatStore();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  useEffect(() => {
    // Load sessions on mount
    loadSessions();

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [loadSessions, cleanup]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Determine department color and gradient based on role/department
  const getDepartmentStyles = () => {
    if (user?.department === 'ALL' || user?.role === 'ADMIN' || user?.role === 'MASTER') {
      return {
        color: '#7c4dff',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      };
    }
    if (user?.department === 'HR') {
      return {
        color: '#1976d2',
        gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      };
    }
    if (user?.department === 'SALES') {
      return {
        color: '#2e7d32',
        gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
      };
    }
    if (user?.department === 'GUEST' || user?.role === 'GUEST') {
      return {
        color: '#f57c00',
        gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
      };
    }
    // Default fallback
    return {
      color: '#757575',
      gradient: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
    };
  };

  const getDepartmentIcon = () => {
    if (user?.department === 'ALL' || user?.role === 'ADMIN' || user?.role === 'MASTER') {
      return <AdminPanelSettingsIcon />;
    }
    if (user?.department === 'HR') {
      return <WorkIcon />;
    }
    if (user?.department === 'SALES') {
      return <StorefrontIcon />;
    }
    if (user?.department === 'GUEST' || user?.role === 'GUEST') {
      return <PublicIcon />;
    }
    return null;
  };

  const { color: departmentColor, gradient: departmentGradient } = getDepartmentStyles();
  const departmentIcon = getDepartmentIcon();
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'vi').split('-')[0];

  const handleLanguageChange = (event) => {
    const value = event.target.value;
    if (value) {
      i18n.changeLanguage(value);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Modern App Bar with glassmorphism */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: {
            xs: '100%',
            md: desktopOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%'
          },
          ml: {
            xs: 0,
            md: desktopOpen ? `${DRAWER_WIDTH}px` : 0
          },
          background: departmentGradient,
          borderBottom: '2px solid rgba(255,255,255,0.15)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top left, rgba(255,255,255,0.15), transparent)',
            pointerEvents: 'none',
          },
        }}
      >
        <Toolbar sx={{ minHeight: 80, position: 'relative' }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: 'none' },
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop toggle button */}
          <IconButton
            color="inherit"
            onClick={() => setDesktopOpen(!desktopOpen)}
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              bgcolor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.25)',
                transform: 'scale(1.1)',
              },
            }}
          >
            {desktopOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="h5"
              noWrap
              sx={{
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {t('chat:title', { defaultValue: 'Coaris Chat' })}
            </Typography>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: 'rgba(255,255,255,0.3)',
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'rgba(255,255,255,0.25)',
                  fontSize: '1rem',
                }}
              >
                {departmentIcon}
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                }}
              >
                {t(`common:status.role.${user?.role?.toLowerCase?.() ||
                  (user?.department === 'HR'
                    ? 'hr'
                    : user?.department === 'SALES'
                      ? 'sales'
                      : user?.department === 'GUEST'
                        ? 'guest'
                        : 'user')
                  }`, {
                  defaultValue:
                    user?.role === 'ADMIN' || user?.role === 'MASTER'
                      ? '👑 Admin'
                      : user?.department === 'HR'
                        ? '👥 HR'
                        : user?.department === 'SALES'
                          ? '📊 Sales'
                          : user?.department === 'GUEST'
                            ? '🌐 Guest'
                            : '👤 User',
                })}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'rgba(255,255,255,0.15)',
              px: 2,
              py: 1,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            {!isMobile && (
              <FormControl size="small" variant="outlined" sx={{ minWidth: 140 }}>
                <InputLabel id="chat-language-select-label" sx={{ color: 'white' }}>
                  {t('common:nav.language')}
                </InputLabel>
                <Select
                  labelId="chat-language-select-label"
                  label={t('common:nav.language')}
                  value={currentLanguage}
                  onChange={handleLanguageChange}
                  sx={{
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '.MuiSelect-icon': {
                      color: 'white',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1a1f36',
                        color: 'white',
                      },
                    },
                  }}
                >
                  <MenuItem value="vi">{t('common:nav.languageOptions.vi')}</MenuItem>
                  <MenuItem value="en">{t('common:nav.languageOptions.en')}</MenuItem>
                  <MenuItem value="ja">{t('common:nav.languageOptions.ja')}</MenuItem>
                </Select>
              </FormControl>
            )}

            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'rgba(255,255,255,0.25)',
                border: '2px solid rgba(255,255,255,0.3)',
                fontWeight: 700,
                fontSize: '0.9rem',
              }}
            >
              {user?.full_name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {user?.full_name}
            </Typography>

            {/* My Documents Button - For GUEST users */}
            {(user?.role === 'GUEST' || user?.department === 'GUEST') && (
              <Tooltip title={t('chat:actions.myDocuments', { defaultValue: 'My Documents' })} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/user-rag')}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <CloudUploadIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Export Conversation Button */}
            {currentSession && messages.length >= 2 && (
              <Box sx={{ color: 'white' }}>
                <ExportConversationButton
                  sessionId={currentSession.id}
                  messageCount={messages.length}
                />
              </Box>
            )}

            {/* Admin Dashboard Button */}
            {(user?.role === 'ADMIN' || user?.role === 'MASTER') && (
              <Tooltip title={t('common:tooltips.adminDashboard')} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/admin')}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    transform: 'scale(1.1)',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.2)',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <DashboardIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Logout Button */}
            <Tooltip title={t('common:nav.logout')} arrow>
              <IconButton
                color="inherit"
                onClick={handleLogout}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: 'rgba(255,0,0,0.2)',
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: {
            xs: 0,
            md: desktopOpen ? DRAWER_WIDTH : 0
          },
          flexShrink: { md: 0 },
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          <Sidebar onClose={handleDrawerToggle} />
        </Drawer>

        {/* Desktop drawer with toggle */}
        <Drawer
          variant="persistent"
          open={desktopOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '4px 0 20px rgba(0,0,0,0.08)',
            },
          }}
        >
          <Sidebar />
        </Drawer>
      </Box>

      {/* Main Content with responsive width */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            xs: '100%',
            md: desktopOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%'
          },
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Toolbar sx={{ minHeight: 80 }} /> {/* Spacer for AppBar */}

        <MessageList />

        <MessageInput />
      </Box>
    </Box >
  );
}
